// Ported from the prototype's OurJourney.js: see there for the sequence. Its motion values are a scroll listener here,
// setting the same CSS variables on the section, and each card's scale. A custom element, so the theme editor's
// re-render of the section tears it down and sets it up again.

// Shares of the lead-in (a viewport of scroll since the photo pinned) over which the photo opens out, the video fades
// in over it, and it darkens, to DARKEST
const FILL = [0, 0.5];
const FADE = [0.25, 0.5];
const DARKEN = [0.125, 0.625];
const DARKEST = 0.6;

// How big a card is at the right edge of the screen; it scales down to full size as it slides into the first card's
// place on the left. Over the last card's step of the slide, every card settles to full size, so the row lines up as
// the section lets go
const MAX_SCALE = 1.15;
// A pause in sideways wheel events this long, in ms, ends a swipe (inertia events come every frame or so)
const SWIPE_GAP = 200;
// Touch: how far a finger moves, in px, before the swipe's direction is decided; how long after its last move a lift
// still flings it on, in ms; and how quickly the fling eases off (its speed falls to about a third over this long, in ms)
const TOUCH_SLOP = 8;
const FLING_WINDOW = 80;
const FLING_EASE = 325;
// The year at the tip of the progress line runs over these, each an equal share of the slide (not tied to the cards)
const FIRST_YEAR = 2016;
const LAST_YEAR = 2026;

const clamp = (n) => Math.min(Math.max(n, 0), 1);
// Where n sits from `from` to `to`, 0 → 1
const between = (n, [from, to]) => clamp((n - from) / (to - from));

class AboutJourney extends HTMLElement {
  connectedCallback() {
    this.lead = this.querySelector(".journey__lead");
    this.pinTrack = this.querySelector(".journey__pin-track");
    this.slideTrack = this.querySelector(".journey__slide-track");
    this.titlePin = this.querySelector(".journey__title-pin");
    this.intro = this.titlePin.querySelector(".journey__intro");
    this.year = this.querySelector(".journey__progress-year");
    this.list = this.querySelector(".journey__cards");
    this.video = this.querySelector(".journey__video video");
    this.cards = [...this.list.children].map((el, i) => new Card(el, this, `${this.id}-preview-${i}`));
    // The step from one card to the next, the run from the first card's place to the right edge of the screen, and the
    // slide distance; set once measured
    this.ramp = null;
    // The share of the screen the title climbs, from the bottom edge to its place in the pin
    this.titleTravel = 1;

    this.onScroll = () => {
      this.frame ||= requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    addEventListener("scroll", this.onScroll, { passive: true });
    this.onWheel = this.onWheel.bind(this);
    addEventListener("wheel", this.onWheel, { passive: false });
    this.stopTouch = this.followTouch();

    // The pin is a viewport tall, so this catches a change in height too. ResizeObserver fires once on observe, so this
    // also does the first measure and update
    this.observer = new ResizeObserver(() => {
      this.measure();
      this.update();
    });
    this.observer.observe(this.list);
    this.observer.observe(this.titlePin);
  }

  disconnectedCallback() {
    removeEventListener("scroll", this.onScroll);
    removeEventListener("wheel", this.onWheel);
    this.stopTouch();
    this.observer.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.swipeEnd);
    this.cards.forEach((card) => card.disconnect());
  }

  // Slides until the last card's right edge lines up with the content column (the same inset as the first card's left)
  measure() {
    const first = this.list.firstElementChild;
    const last = this.list.lastElementChild;
    const width = this.list.clientWidth;
    this.ramp = {
      step: first.nextElementSibling.offsetLeft - first.offsetLeft,
      run: width - first.offsetLeft,
      slide: Math.max(0, last.offsetLeft + last.offsetWidth + first.offsetLeft - width),
    };
    this.style.setProperty("--distance", this.ramp.slide);

    const titleTop = this.intro.getBoundingClientRect().top - this.titlePin.getBoundingClientRect().top;
    this.titleTravel = (innerHeight - titleTop) / innerHeight;
  }

  update() {
    // 0 as the photo pins (the copy at the bottom of the screen), 1 once the copy reaches the top
    const lead = this.lead.getBoundingClientRect();
    const leadIn = clamp(-lead.top / lead.height);
    this.style.setProperty("--fill", between(leadIn, FILL));
    this.style.setProperty("--darken", between(leadIn, DARKEN) * DARKEST);
    const video = between(leadIn, FADE);
    this.style.setProperty("--video", video);
    this.loopVideo(video);

    // 0 as the title's pin enters the bottom of the screen, 1 as it pins
    const arrive = clamp((innerHeight - this.pinTrack.getBoundingClientRect().top) / innerHeight);
    this.style.setProperty("--title", clamp(1 - (1 - arrive) / this.titleTravel));

    // 0 as the cards pin, 1 as they let go: that stretch is exactly the slide distance of scroll, so they move 1:1
    const slideTrack = this.slideTrack.getBoundingClientRect();
    const room = slideTrack.height - innerHeight;
    const slide = room > 0 ? clamp(-slideTrack.top / room) : Number(slideTrack.top <= 0);
    this.style.setProperty("--progress", slide);
    const year = String(Math.min(LAST_YEAR, FIRST_YEAR + Math.floor(slide * (LAST_YEAR - FIRST_YEAR + 1))));
    if (this.year.textContent !== year) this.year.textContent = year;

    if (!this.ramp) return;
    const x = -slide * this.ramp.slide;
    this.list.style.translate = `${x}px`;
    this.cards.forEach((card, i) => card.place(i, x, this.ramp));
  }

  // Plays the muted loop from the top as it begins to fade in, and stops and rewinds it once it has faded back out
  loopVideo(opacity) {
    const video = this.video;
    if (!video) return;
    if (opacity > 0 && video.paused) {
      video.muted = true;
      video.play().catch(() => {});
    } else if (opacity === 0 && !video.paused) {
      video.pause();
      video.currentTime = 0;
    }
  }

  // Scrolls so the card sits in the first card's place, or as near as the slide goes for the last few. The slide runs
  // 1:1 with the scroll, so that's a step's scroll per card from where the cards pin
  scrollToCard(card) {
    const start = this.slideTrack.getBoundingClientRect().top + scrollY;
    const index = this.cards.indexOf(card);
    scrollTo({ top: start + Math.min(index * this.ramp.step, this.ramp.slide), behavior: "smooth" });
  }

  // Side scrolling: a sideways swipe (trackpad, or shift + wheel) that starts over the cards while they're pinned
  // scrolls the page by as much, and the slide follows 1:1. The whole gesture is followed, its inertia included, even
  // once the cards let go at either end; it ends after a pause in the events, or when the scrolling turns mostly
  // vertical. Instant, as the theme's smooth scrolling would lag behind a swipe. Taking the swipe also stops it going
  // back a page in the browser's history
  onWheel(e) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
      this.swiping = false;
      return;
    }
    if (!this.swiping) {
      const { top, bottom } = this.slideTrack.getBoundingClientRect();
      if (!this.slideTrack.contains(e.target) || top > 0 || bottom < innerHeight) return;
      this.swiping = true;
    }
    clearTimeout(this.swipeEnd);
    this.swipeEnd = setTimeout(() => (this.swiping = false), SWIPE_GAP);
    e.preventDefault();
    const lines = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : 1;
    scrollBy({ top: e.deltaX * lines, behavior: "instant" });
  }

  // Touch: as with the wheel, a sideways swipe that starts over the cards while they're pinned scrolls the page by as
  // much, the cards following 1:1. Up and down swipes are the browser's (the cards are pan-y), so it only picks up a
  // swipe that sets off more sideways than not. Let go mid-swipe, it flings on at the finger's speed and eases to a
  // stop, as the browser's own scrolling does; a new touch stops it. Returns a function that stops following
  followTouch() {
    const track = this.slideTrack;
    // Where the touch started, until it's decided; then the finger's last move, while it's a sideways swipe
    let start = null;
    let last = null;
    let speed = 0;
    let frame = 0;

    const onStart = (e) => {
      cancelAnimationFrame(frame);
      const { top, bottom } = track.getBoundingClientRect();
      const touch = e.touches[0];
      start = e.touches.length === 1 && top <= 0 && bottom >= innerHeight ? { x: touch.clientX, y: touch.clientY } : null;
      last = null;
    };

    const onMove = (e) => {
      const touch = e.touches[0];
      if (start) {
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.hypot(dx, dy) < TOUCH_SLOP) return;
        if (Math.abs(dx) > Math.abs(dy)) last = { x: touch.clientX, time: e.timeStamp };
        start = null;
        speed = 0;
        return;
      }
      if (!last) return;
      const dx = touch.clientX - last.x;
      const dt = e.timeStamp - last.time;
      scrollBy({ top: -dx, behavior: "instant" });
      if (dt > 0) speed = -dx / dt;
      last = { x: touch.clientX, time: e.timeStamp };
    };

    const onEnd = (e) => {
      if (!last) return;
      const held = e.timeStamp - last.time > FLING_WINDOW;
      last = null;
      if (held) return;
      // px per ms, falling away exponentially
      const from = performance.now();
      let prev = from;
      const fling = (now) => {
        const v = speed * Math.exp(-(now - from) / FLING_EASE);
        if (Math.abs(v) < 0.02) return;
        scrollBy({ top: v * (now - prev), behavior: "instant" });
        prev = now;
        frame = requestAnimationFrame(fling);
      };
      frame = requestAnimationFrame(fling);
    };

    track.addEventListener("touchstart", onStart, { passive: true });
    track.addEventListener("touchmove", onMove, { passive: true });
    track.addEventListener("touchend", onEnd);
    return () => {
      track.removeEventListener("touchstart", onStart);
      track.removeEventListener("touchmove", onMove);
      track.removeEventListener("touchend", onEnd);
      cancelAnimationFrame(frame);
    };
  }
}

// When the copy runs past 3 lines, Read more grows it in place, pushing the title up over the photo (as far as the
// year), then the year, title and copy all scroll together. Clicking a card slides it into the first card's place and
// opens it if there's more to read; Read more only opens and closes it where it is
class Card {
  constructor(el, journey, previewId) {
    this.el = el;
    this.journey = journey;
    this.scroller = el.querySelector(".journey__card-scroll");
    this.top = el.querySelector(".journey__card-top");
    this.body = el.querySelector(".journey__card-body");
    this.preview = el.querySelector(".journey__preview");
    this.more = el.querySelector(".journey__more");
    this.icon = this.more.querySelector("path");
    this.preview.id = previewId;
    this.more.setAttribute("aria-controls", previewId);
    // closed → opening → open → closing → closed. The card's contents scroll only while open
    this.phase = "closed";
    this.hasMore = false;

    this.more.addEventListener("click", () => this.toggle());
    // Links and Read more do their own thing; an open card is still brought in, and stays open
    el.addEventListener("click", (e) => {
      if (!journey.ramp || e.target.closest("a, button")) return;
      journey.scrollToCard(this);
      if (this.hasMore && !this.open) this.setPhase("opening");
    });
    el.addEventListener("transitionend", (e) => {
      if (e.target === el && e.propertyName === "--open") this.setPhase(this.open ? "open" : "closed");
    });

    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.preview);
    this.observer.observe(this.body);
  }

  get open() {
    return this.phase === "opening" || this.phase === "open";
  }

  disconnect() {
    this.observer.disconnect();
  }

  // How much more copy there is past the first 3 lines, and the room above the title to grow into. Only while closed,
  // when the title is in its place. More than a gap between paragraphs, so copy that's 3 lines in all isn't counted
  measure() {
    if (this.phase !== "closed") return;
    const lineHeight = parseFloat(getComputedStyle(this.preview).lineHeight);
    const overflow = this.preview.scrollHeight - 3 * lineHeight;
    this.hasMore = overflow > lineHeight / 2;
    this.el.toggleAttribute("data-more", this.hasMore);
    this.more.style.visibility = this.hasMore ? "" : "hidden";
    if (this.hasMore) {
      this.el.style.setProperty("--overflow", `${overflow}px`);
      this.el.style.setProperty("--room", `${this.body.offsetTop - this.top.offsetTop - this.top.offsetHeight}px`);
    } else {
      this.el.style.removeProperty("--overflow");
      this.el.style.removeProperty("--room");
    }
  }

  setPhase(phase) {
    this.phase = phase;
    this.el.toggleAttribute("data-open", this.open);
    this.scroller.toggleAttribute("data-scroll", phase === "open");
    this.more.setAttribute("aria-expanded", this.open);
    this.icon.setAttribute("d", this.open ? "M5 12h14" : "M12 5v14M5 12h14");
    if (phase === "closed") this.measure();
  }

  toggle() {
    if (this.open) {
      this.scroller.scrollTop = 0;
      this.setPhase("closing");
    } else {
      this.setPhase("opening");
    }
  }

  // Its scale as the row slides by x. How far right of the first card's place it sits, as a share of the run to the
  // screen's right edge: full size there and anywhere left of it, MAX_SCALE at the edge and beyond, brought back to
  // full size over the slide's last step
  place(index, x, ramp) {
    const along = clamp((index * ramp.step + x) / ramp.run);
    const remaining = clamp((ramp.slide + x) / ramp.step);
    this.el.style.scale = 1 + (MAX_SCALE - 1) * along * remaining;
  }
}

if (!customElements.get("about-journey")) customElements.define("about-journey", AboutJourney);
